import React from "react";
import moment from "moment";
import {
  BREAKUP_LABELS,
  branchTitle,
  formatReportDate,
  moneyDec,
  num,
} from "./misReportShared";
import "./MISReportPrint.css";

const Section = ({ title, children }) => (
  <div className="mis-p-section">
    <div className="mis-p-section-title">{title}</div>
    {children}
  </div>
);

const Footer = ({ branchLabel, generatedAt }) => (
  <div className="mis-p-footer">
    <span>Vostro World · {branchLabel} · Confidential management report</span>
    <span>Generated {moment(generatedAt).format("DD MMM YYYY, HH:mm")}</span>
  </div>
);

const SalesRow = ({ label, row }) => {
  const r = row || { qty: 0, price: 0, discount: 0, tax: 0, net: 0 };
  return (
    <tr>
      <td>{label}</td>
      <td className="num">{r.qty ?? 0}</td>
      <td className="num">{moneyDec(r.price)}</td>
      <td className="num">{moneyDec(r.discount)}</td>
      <td className="num">{moneyDec(r.tax)}</td>
      <td className="num">{moneyDec(r.net)}</td>
    </tr>
  );
};

function MoneyBarChart({ items }) {
  if (!items.length) {
    return <p className="mis-p-note">No sales in this period.</p>;
  }
  const max = Math.max(...items.map((i) => Number(i.net || 0)), 1);
  return (
    <div>
      {items.map((item) => (
        <div key={item.name} className="mis-p-bar-row">
          <span className="mis-p-bar-label">{item.name}</span>
          <div className="mis-p-bar-track">
            <div
              className="mis-p-bar-fill"
              style={{ width: `${(Number(item.net || 0) / max) * 100}%` }}
            />
          </div>
          <span className="mis-p-bar-val">{moneyDec(item.net)}</span>
        </div>
      ))}
    </div>
  );
}

function HBarChart({ items, valueKey = "value" }) {
  const max = Math.max(...items.map((i) => Number(i[valueKey] || 0)), 1);
  return (
    <div>
      {items.map((item) => (
        <div key={item.name} className="mis-p-bar-row">
          <span className="mis-p-bar-label">{item.name}</span>
          <div className="mis-p-bar-track">
            <div
              className="mis-p-bar-fill"
              style={{
                width: `${(Number(item[valueKey] || 0) / max) * 100}%`,
                background: "#0f766e",
              }}
            />
          </div>
          <span className="mis-p-bar-val">{num(item[valueKey])}</span>
        </div>
      ))}
    </div>
  );
}

export default function MISReportPrintDocument({ data }) {
  if (!data?.meta) return null;

  const meta = data.meta;
  const exec = data.executive || {};
  const foot = data.footfall || {};
  const hr = data.hr || {};
  const dept = data.departments || {};
  const breakup = data.sales?.breakup_today || {};
  const today = data.sales?.today || {};
  const mtd = data.sales?.mtd || {};
  const cafe = data.cafe || {};
  const trainersToday = data.trainers?.top_today || [];
  const trainersMtd = data.trainers?.top_mtd || [];
  const activeAsOfLabel = meta.date ? moment(meta.date).format("DD MMM YYYY") : "";
  const oldest = data.membership?.oldest_active_packages || [];
  const byBranch = data.by_branch || [];
  const trend = data.trend || [];
  const social = dept.social_leads || {};
  const fy = data.fiscal_year || {};
  const fyMonthly = fy.monthly || [];
  const fyServicePackages = fy.top_service_packages || fy.top_packages || [];
  const fyCafeProducts = fy.top_cafe_products || [];
  const physioDetail = dept.physio_detail || {};
  const nutritionDetail = dept.nutrition_detail || {};

  const sumBreakup = (keys) => {
    let qty = 0;
    let net = 0;
    keys.forEach((k) => {
      qty += Number(breakup[k]?.qty || 0);
      net += Number(breakup[k]?.net || 0);
    });
    return { qty, net: Math.round(net * 100) / 100 };
  };
  const gym = sumBreakup(["gym_new", "gym_renew"]);
  const pt = sumBreakup(["pt_new", "pt_renew"]);

  const departmentOverview = [
    {
      name: "Gym membership",
      qty: gym.qty,
      todayNet: gym.net,
      mtdNet: "—",
      activity: `${num(exec.active_gym_members)} gym active${activeAsOfLabel ? ` as of ${activeAsOfLabel}` : ""}`,
    },
    {
      name: "Personal training",
      qty: pt.qty,
      todayNet: pt.net,
      mtdNet: "—",
      activity: `${num(hr.pt_staff_present)} trainers present`,
    },
    {
      name: "GX / Vitality studio",
      qty: breakup.gx?.qty ?? 0,
      todayNet: breakup.gx?.net ?? 0,
      mtdNet: "—",
      activity: `${num(foot.studio_total)} studio check-ins`,
    },
    {
      name: "Nutrition",
      qty: breakup.nutrition?.qty ?? 0,
      todayNet: breakup.nutrition?.net ?? 0,
      mtdNet: dept.nutrition?.mtd_net ?? 0,
      activity: `${num(dept.nutrition?.appointments_today)} appointments`,
    },
    {
      name: "Physiotherapy",
      qty: breakup.physio?.qty ?? 0,
      todayNet: breakup.physio?.net ?? 0,
      mtdNet: dept.physio?.mtd_net ?? 0,
      activity: `${num(dept.physio?.appointments_today)} appointments`,
    },
    {
      name: "Fitness academy",
      qty: breakup.academy?.qty ?? 0,
      todayNet: breakup.academy?.net ?? 0,
      mtdNet: "—",
      activity: "Training programmes",
    },
    {
      name: "Café",
      qty: breakup.cafe?.qty ?? 0,
      todayNet: breakup.cafe?.net ?? 0,
      mtdNet: exec.cafe_mtd_net,
      activity: `${num(cafe.meals)} meals · ${num(cafe.drinks)} drinks`,
    },
    {
      name: "Other sales",
      qty: breakup.other?.qty ?? 0,
      todayNet: breakup.other?.net ?? 0,
      mtdNet: "—",
      activity: "Misc. categories",
    },
    {
      name: "Social / leads",
      qty: social.leads_today ?? 0,
      todayNet: "—",
      mtdNet: "—",
      activity: `${num(social.leads_today)} leads · ${num(social.payments)} paid · ${num(social.visit_completed)} visits`,
    },
  ];

  const breakupChart = Object.entries(BREAKUP_LABELS)
    .map(([key, name]) => ({
      name: name.length > 22 ? name.slice(0, 20) + "…" : name,
      net: Number(breakup[key]?.net || 0),
    }))
    .filter((r) => r.net > 0);

  const footfallChart = [
    { name: "Morning", value: foot.morning },
    { name: "Afternoon", value: foot.afternoon },
    { name: "Evening", value: foot.evening },
  ];

  const lateStaff = hr.late_staff || [];
  const absentStaff = hr.absent_staff || [];
  const leaveStaff = hr.leave_staff || [];

  return (
    <div className="mis-print-root" aria-hidden="true">
      <div className="mis-print-slice">
        <div className="mis-p-banner">
          <h1>{branchTitle(meta.branch_label)}</h1>
          <p>
            <strong>Report date:</strong> {formatReportDate(meta.date)} ·{" "}
            <strong>Month to date:</strong> {moment(meta.month_start).format("DD MMM YYYY")} –{" "}
            {moment(meta.date).format("DD MMM YYYY")}
          </p>
          <p>Sales net = price + tax (Detailed Sales Report). Discounts shown separately in sales tables.</p>
        </div>

        <div className="mis-p-kpi-grid">
          <div className="mis-p-kpi">
            <label>Today sales</label>
            <strong>Rs {moneyDec(exec.sales_today_net)}</strong>
            <span>Services {moneyDec(exec.services_today_net)} · Café {moneyDec(exec.cafe_today_net)}</span>
          </div>
          <div className="mis-p-kpi">
            <label>Month sales</label>
            <strong>Rs {moneyDec(exec.sales_mtd_net)}</strong>
            <span>Services {moneyDec(exec.services_mtd_net)} · Café {moneyDec(exec.cafe_mtd_net)}</span>
          </div>
          <div className="mis-p-kpi">
            <label>Today profit</label>
            <strong>Rs {moneyDec(exec.profit_today)}</strong>
            <span>Expenses today Rs {moneyDec(exec.expenses_today)}</span>
          </div>
          <div className="mis-p-kpi">
            <label>{activeAsOfLabel ? `Active clients (${activeAsOfLabel})` : "Active clients"}</label>
            <strong>{num(exec.active_clients)}</strong>
            <span>Any service · {num(exec.active_packages)} lines · Gym {num(exec.active_gym_members)}</span>
          </div>
          <div className="mis-p-kpi">
            <label>Footfall</label>
            <strong>{num(foot.total_checkins)}</strong>
            <span>
              M/F {num(foot.total_males)}/{num(foot.total_females)} · Absent paid {num(foot.absent_paid_gym)}
            </span>
          </div>
          <div className="mis-p-kpi">
            <label>Staff</label>
            <strong>
              {num(hr.present)}/{num(hr.total_staff)}
            </strong>
            <span>
              Absent {num(hr.absent)} · Late {num(hr.late)} · Leave {num(hr.leave_count)}
            </span>
          </div>
        </div>

        <Section title="All departments — overview">
          <table className="mis-p-table">
            <thead>
              <tr>
                <th>Department</th>
                <th className="num">Today qty</th>
                <th className="num">Today net (Rs)</th>
                <th className="num">MTD / reference</th>
                <th>Activity &amp; notes</th>
              </tr>
            </thead>
            <tbody>
              {departmentOverview.map((d) => (
                <tr key={d.name}>
                  <td>
                    <strong>{d.name}</strong>
                  </td>
                  <td className="num">{d.qty}</td>
                  <td className="num">
                    {d.todayNet === "—" ? "—" : moneyDec(d.todayNet)}
                  </td>
                  <td className="num">
                    {d.mtdNet === "—" ? "—" : typeof d.mtdNet === "number" ? moneyDec(d.mtdNet) : d.mtdNet}
                  </td>
                  <td>{d.activity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <div className="mis-p-two-col">
          <Section title="Finance">
            <table className="mis-p-table">
              <tbody>
                <tr>
                  <td>Sales MTD (net)</td>
                  <td className="num">Rs {moneyDec(exec.sales_mtd_net)}</td>
                </tr>
                <tr>
                  <td>Expenses MTD</td>
                  <td className="num">Rs {moneyDec(exec.expenses_mtd)}</td>
                </tr>
                <tr>
                  <td>Expenses today</td>
                  <td className="num">Rs {moneyDec(exec.expenses_today)}</td>
                </tr>
                <tr>
                  <td>Profit today</td>
                  <td className="num">Rs {moneyDec(exec.profit_today)}</td>
                </tr>
                <tr>
                  <td>Pending expense approvals</td>
                  <td className="num">{num(exec.pending_expense_approvals)}</td>
                </tr>
              </tbody>
            </table>
          </Section>

          {meta.is_all_branches && byBranch.length ? (
            <Section title="Branch comparison (today)">
              <table className="mis-p-table">
                <thead>
                  <tr>
                    <th>Branch</th>
                    <th className="num">Sales</th>
                    <th className="num">Footfall</th>
                    <th className="num">Staff</th>
                  </tr>
                </thead>
                <tbody>
                  {byBranch.map((b) => (
                    <tr key={b.branch_id}>
                      <td>{b.branch_label}</td>
                      <td className="num">{moneyDec(b.total_sales_today)}</td>
                      <td className="num">{num(b.totalCheckins)}</td>
                      <td className="num">{num(b.presentStaff)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          ) : (
            <Section title="Membership snapshot">
              <table className="mis-p-table">
                <tbody>
                  <tr>
                    <td>Active clients (all services)</td>
                    <td className="num">{num(exec.active_clients)}</td>
                  </tr>
                  <tr>
                    <td>Active packages</td>
                    <td className="num">{num(exec.active_packages)}</td>
                  </tr>
                  <tr>
                    <td>Walk-ins / visitors</td>
                    <td className="num">{num(foot.visitors_walkins)}</td>
                  </tr>
                </tbody>
              </table>
            </Section>
          )}
        </div>

        <Footer branchLabel={meta.branch_label} generatedAt={meta.generated_at} />
      </div>

      <div className="mis-print-slice">
        <Section title="Sales summary">
          <table className="mis-p-table">
            <thead>
              <tr>
                <th>Particulars</th>
                <th className="num">Qty</th>
                <th className="num">Price</th>
                <th className="num">Discount</th>
                <th className="num">GST</th>
                <th className="num">Net</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Sales month-to-date</td>
                <td className="num">{mtd.qty ?? 0}</td>
                <td className="num">{moneyDec(mtd.price)}</td>
                <td className="num">{moneyDec(mtd.discount)}</td>
                <td className="num">{moneyDec(mtd.tax)}</td>
                <td className="num">{moneyDec(mtd.net)}</td>
              </tr>
              <tr className="total">
                <td>Today total</td>
                <td className="num">{today.qty ?? 0}</td>
                <td className="num">{moneyDec(today.price)}</td>
                <td className="num">{moneyDec(today.discount)}</td>
                <td className="num">{moneyDec(today.tax)}</td>
                <td className="num">{moneyDec(today.net)}</td>
              </tr>
            </tbody>
          </table>
        </Section>

        <Section title="Daily sales breakup by department">
          <table className="mis-p-table">
            <thead>
              <tr>
                <th>Particulars</th>
                <th className="num">Qty</th>
                <th className="num">Price</th>
                <th className="num">Discount</th>
                <th className="num">GST</th>
                <th className="num">Net</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(BREAKUP_LABELS).map(([key, label]) => (
                <SalesRow key={key} label={label} row={breakup[key]} />
              ))}
              <tr className="total">
                <td>Grand total</td>
                <td className="num">{today.qty ?? 0}</td>
                <td className="num">{moneyDec(today.price)}</td>
                <td className="num">{moneyDec(today.discount)}</td>
                <td className="num">{moneyDec(today.tax)}</td>
                <td className="num">{moneyDec(today.net)}</td>
              </tr>
            </tbody>
          </table>
        </Section>
      </div>

      <div className="mis-print-slice">
        <div className="mis-p-two-col">
          <div>
            <p className="mis-p-chart-title">Sales by department (today net)</p>
            <MoneyBarChart items={breakupChart} />
          </div>
          <div>
            <p className="mis-p-chart-title">Last 7 days — sales (Rs net)</p>
            {trend.length ? (
              trend.map((row) => (
                <div key={row.label} className="mis-p-trend-row">
                  <span>{row.label}</span>
                  <div className="mis-p-bar-track">
                    <div
                      className="mis-p-bar-fill"
                      style={{
                        width: `${Math.min(100, (Number(row.sales || 0) / Math.max(...trend.map((t) => t.sales || 0), 1)) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className="mis-p-bar-val">{moneyDec(row.sales)}</span>
                </div>
              ))
            ) : (
              <p className="mis-p-note">No trend data</p>
            )}
          </div>
        </div>

        <Footer branchLabel={meta.branch_label} generatedAt={meta.generated_at} />
      </div>

      <div className="mis-print-slice">
        <div className="mis-p-two-col">
          <Section title="Footfall">
            <table className="mis-p-table">
              <tbody>
                <tr>
                  <td>Morning (7–12)</td>
                  <td className="num">{num(foot.morning)}</td>
                </tr>
                <tr>
                  <td>Afternoon (12–5)</td>
                  <td className="num">{num(foot.afternoon)}</td>
                </tr>
                <tr>
                  <td>Evening (5–11)</td>
                  <td className="num">{num(foot.evening)}</td>
                </tr>
                <tr>
                  <td>Total check-ins</td>
                  <td className="num">{num(foot.total_checkins)}</td>
                </tr>
                <tr>
                  <td>Females / males</td>
                  <td className="num">
                    {num(foot.total_females)} / {num(foot.total_males)}
                  </td>
                </tr>
                <tr>
                  <td>Busiest / slowest</td>
                  <td>
                    {foot.busiest_slot || "—"} / {foot.slowest_slot || "—"}
                  </td>
                </tr>
                <tr>
                  <td>Absent paid (gym)</td>
                  <td className="num">{num(foot.absent_paid_gym)}</td>
                </tr>
                <tr>
                  <td>GX studio total</td>
                  <td className="num">{num(foot.studio_total)}</td>
                </tr>
              </tbody>
            </table>
            <p className="mis-p-chart-title">Footfall chart</p>
            <HBarChart items={footfallChart} />
          </Section>

          <Section title="Café · HR · Personal training">
            <table className="mis-p-table">
              <tbody>
                <tr className="dept-head">
                  <td colSpan={2}>Café</td>
                </tr>
                <tr>
                  <td>Net sales</td>
                  <td className="num">Rs {moneyDec(cafe.net_today)}</td>
                </tr>
                <tr>
                  <td>Meals / drinks / sides</td>
                  <td className="num">
                    {num(cafe.meals)} / {num(cafe.drinks)} / {num(cafe.sides)}
                  </td>
                </tr>
                <tr className="dept-head">
                  <td colSpan={2}>Human resources</td>
                </tr>
                <tr>
                  <td>Present / total</td>
                  <td className="num">
                    {num(hr.present)} / {num(hr.total_staff)}
                  </td>
                </tr>
                <tr>
                  <td>Absent / late / leave</td>
                  <td className="num">
                    {num(hr.absent)} / {num(hr.late)} / {num(hr.leave_count)}
                  </td>
                </tr>
                <tr className="dept-head">
                  <td colSpan={2}>Personal training</td>
                </tr>
                <tr>
                  <td>Trainers present</td>
                  <td className="num">{num(hr.pt_staff_present)}</td>
                </tr>
                <tr>
                  <td>PT new / renew (qty)</td>
                  <td className="num">
                    {breakup.pt_new?.qty ?? 0} / {breakup.pt_renew?.qty ?? 0}
                  </td>
                </tr>
              </tbody>
            </table>
          </Section>
        </div>
      </div>

      <div className="mis-print-slice">
        <Section title="Top PT trainers — today &amp; month">
          <table className="mis-p-table">
            <thead>
              <tr>
                <th>Trainer</th>
                <th className="num">Today qty</th>
                <th className="num">Today net</th>
                <th className="num">MTD qty</th>
                <th className="num">MTD net</th>
              </tr>
            </thead>
            <tbody>
              {trainersMtd.length ? (
                trainersMtd.map((t) => {
                  const todayRow = trainersToday.find((x) => x.trainer_id === t.trainer_id);
                  return (
                    <tr key={t.trainer_id}>
                      <td>{t.trainer_name}</td>
                      <td className="num">{todayRow?.qty ?? 0}</td>
                      <td className="num">{moneyDec(todayRow?.net ?? 0)}</td>
                      <td className="num">{t.qty}</td>
                      <td className="num">{moneyDec(t.net)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5}>No PT sales this month</td>
                </tr>
              )}
            </tbody>
          </table>
        </Section>
      </div>

      <div className="mis-print-slice">
        <div className="mis-p-two-col">
          <Section title="Longest active packages">
            <table className="mis-p-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Started</th>
                </tr>
              </thead>
              <tbody>
                {oldest.slice(0, 8).map((row) => (
                  <tr key={row.order_detail_id}>
                    <td>
                      {row.client_name}
                      <br />
                      <span style={{ fontSize: "8px", color: "#64748b" }}>{row.package_name}</span>
                    </td>
                    <td>{row.start_date ? moment(row.start_date).format("DD-MM-YY") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Staff late / absent / leave">
            <table className="mis-p-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {[...lateStaff, ...absentStaff, ...leaveStaff].length ? (
                  [...lateStaff, ...absentStaff, ...leaveStaff].slice(0, 16).map((row, i) => (
                    <tr key={`${row.user_id || row.name}-${i}`}>
                      <td>
                        {row.name}
                        <br />
                        <span style={{ fontSize: "8px", color: "#64748b" }}>{row.department}</span>
                      </td>
                      <td>{row.status}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2}>All staff accounted for</td>
                  </tr>
                )}
              </tbody>
            </table>
          </Section>
        </div>

        <Footer branchLabel={meta.branch_label} generatedAt={meta.generated_at} />
      </div>

      <div className="mis-print-slice mis-print-fy">
        <div className="mis-p-banner" style={{ background: "linear-gradient(135deg, #1e40af 0%, #2563eb 100%)" }}>
          <h1>Fiscal year performance</h1>
          <p>
            <strong>{fy.label || "Fiscal year"}</strong> · {fy.period_label || "—"}
          </p>
          <p>
            Sales net and expenses by calendar month from fiscal year start through the selected report month (full
            months). Sales use Detailed Sales rules (price + tax).
          </p>
        </div>

        <div className="mis-p-kpi-grid">
          <div className="mis-p-kpi" style={{ borderTopColor: "#2563eb" }}>
            <label>Period sales (net)</label>
            <strong>Rs {moneyDec(fy.totals?.sales_net)}</strong>
            <span>{num(fy.totals?.sales_qty)} transactions</span>
          </div>
          <div className="mis-p-kpi" style={{ borderTopColor: "#2563eb" }}>
            <label>Period expenses</label>
            <strong>Rs {moneyDec(fy.totals?.expenses)}</strong>
            <span>Approved expenses only</span>
          </div>
          <div className="mis-p-kpi" style={{ borderTopColor: "#2563eb" }}>
            <label>Period profit</label>
            <strong>Rs {moneyDec(fy.totals?.profit)}</strong>
            <span>Sales net minus expenses</span>
          </div>
        </div>

        <Section title="Month-wise sales &amp; expenses">
          <table className="mis-p-table">
            <thead>
              <tr>
                <th>Month</th>
                <th className="num">Qty</th>
                <th className="num">Sales net</th>
                <th className="num">Expenses</th>
                <th className="num">Profit</th>
              </tr>
            </thead>
            <tbody>
              {fyMonthly.map((m) => (
                <tr key={m.key}>
                  <td>
                    {m.label}
                    {m.is_partial ? " *" : ""}
                  </td>
                  <td className="num">{num(m.sales_qty)}</td>
                  <td className="num">{moneyDec(m.sales_net)}</td>
                  <td className="num">{moneyDec(m.expenses)}</td>
                  <td className="num">{moneyDec(m.profit)}</td>
                </tr>
              ))}
              <tr className="total">
                <td>Total</td>
                <td className="num">{num(fy.totals?.sales_qty)}</td>
                <td className="num">{moneyDec(fy.totals?.sales_net)}</td>
                <td className="num">{moneyDec(fy.totals?.expenses)}</td>
                <td className="num">{moneyDec(fy.totals?.profit)}</td>
              </tr>
            </tbody>
          </table>
          <p className="mis-p-chart-title">Sales by month (net Rs)</p>
          <MoneyBarChart
            items={fyMonthly.map((m) => ({ name: m.label, net: m.sales_net }))}
          />
        </Section>
      </div>

      <div className="mis-print-slice mis-print-fy">
        <Section title="Top gym &amp; services (fiscal period)">
          <table className="mis-p-table">
            <thead>
              <tr>
                <th>Package</th>
                <th className="num">Qty</th>
                <th className="num">Net</th>
              </tr>
            </thead>
            <tbody>
              {fyServicePackages.length ? (
                fyServicePackages.map((p) => (
                  <tr key={`svc-${p.package_id}`}>
                    <td>{p.package_name}</td>
                    <td className="num">{p.qty}</td>
                    <td className="num">{moneyDec(p.net)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3}>No service sales</td>
                </tr>
              )}
            </tbody>
          </table>
        </Section>

        <Section title="Top café products (fiscal period)">
          <table className="mis-p-table">
            <thead>
              <tr>
                <th>Product</th>
                <th className="num">Qty</th>
                <th className="num">Net</th>
              </tr>
            </thead>
            <tbody>
              {fyCafeProducts.length ? (
                fyCafeProducts.map((p) => (
                  <tr key={`cafe-${p.package_id}`}>
                    <td>{p.package_name}</td>
                    <td className="num">{p.qty}</td>
                    <td className="num">{moneyDec(p.net)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3}>No café sales</td>
                </tr>
              )}
            </tbody>
          </table>
        </Section>

        <Section title="Physio &amp; nutrition detail">
          <table className="mis-p-table">
            <tbody>
              <tr className="dept-head">
                <td colSpan={2}>Physiotherapy</td>
              </tr>
              <tr>
                <td>Appointments today / MTD</td>
                <td className="num">
                  {num(physioDetail.appointments_today)} / {num(physioDetail.appointments_mtd)}
                </td>
              </tr>
              <tr>
                <td>Consultations conducted today / MTD</td>
                <td className="num">
                  {num(physioDetail.consultations_conducted_today)} / {num(physioDetail.consultations_conducted_mtd)}
                </td>
              </tr>
              <tr className="dept-head">
                <td colSpan={2}>Nutrition</td>
              </tr>
              <tr>
                <td>Meal plans issued today / MTD</td>
                <td className="num">
                  {num(nutritionDetail.meal_plans_issued_today)} / {num(nutritionDetail.meal_plans_issued_mtd)}
                </td>
              </tr>
              <tr>
                <td>Consultations today / MTD · Conversions MTD</td>
                <td className="num">
                  {num(nutritionDetail.consultations_today)} / {num(nutritionDetail.consultations_mtd)} ·{" "}
                  {num(nutritionDetail.conversions_mtd)}
                </td>
              </tr>
            </tbody>
          </table>
        </Section>

        <Footer branchLabel={meta.branch_label} generatedAt={meta.generated_at} />
      </div>
    </div>
  );
}
